import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { blogPosts as mockBlogPosts } from '../mock';
import api from '../services/api';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);

  useEffect(() => {
    const loadPosts = async () => {
      const data = await api.getBlogPosts(mockBlogPosts);
      if (data.length > 0) setBlogPosts(data);
    };
    loadPosts();
  }, []);

  const featuredPost = blogPosts[0];
  const otherPosts = blogPosts.slice(1);

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              Blog & Insights
            </Badge>
            <h1 className="text-5xl font-bold">
              Conhecimento Especializado em Reforma Predial
            </h1>
            <p className="text-xl text-blue-100">
              Artigos, guias e dicas de especialistas para ajudar síndicos e gestores condominiais a tomar as melhores decisões.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <Badge className="bg-[#F47A20] hover:bg-[#e66a10] text-white mb-6">
              Artigo em Destaque
            </Badge>
            
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="grid lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-[#15448A] text-white">
                      {featuredPost.category}
                    </Badge>
                  </div>
                </div>
                
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center space-y-6">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{featuredPost.date}</span>
                    <span>•</span>
                    <div className="flex items-center space-x-1">
                      <Clock size={16} />
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  
                  <h2 className="text-3xl font-bold text-gray-900">
                    {featuredPost.title}
                  </h2>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {featuredPost.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="border-gray-300">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <Link to={`/blog/${featuredPost.slug}`}>
                    <Button className="bg-gradient-to-r from-[#15448A] to-[#0d2f5e] hover:from-blue-700 hover:to-blue-800 text-white">
                      Ler Artigo Completo
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Other Posts */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-4">Mais Artigos</h2>
            <p className="text-lg text-gray-600">
              Continue explorando nosso conteúdo especializado em reforma e manutenção predial.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherPosts.map((post) => (
              <Card key={post.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-[#15448A] text-white shadow-lg">
                      {post.category}
                    </Badge>
                  </div>
                </div>
                
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{post.date}</span>
                    <span>•</span>
                    <div className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#15448A] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 2).map((tag, index) => (
                      <Badge key={index} variant="outline" className="border-gray-300 text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="ghost" className="w-full justify-between text-[#15448A] hover:text-[#15448A] hover:bg-[#15448A]/5">
                      Ler mais
                      <ArrowRight size={18} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block p-4 bg-white/10 rounded-full mb-4">
              <Tag size={32} />
            </div>
            <h2 className="text-4xl font-bold">
              Fique por Dentro das Novidades
            </h2>
            <p className="text-xl text-blue-100">
              Entre em contato conosco para receber conteúdo especializado e dicas exclusivas sobre reforma e manutenção predial.
            </p>
            <Link to="/contato">
              <Button size="lg" className="bg-white text-[#15448A] hover:bg-gray-100 shadow-xl">
                Entrar em Contato
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;